// BUG-523: when a newer release is live, the tab reloads itself on the NEXT page change, never in
// the middle of what the shopper is doing. Nothing is lost by it: the basket is saved in
// localStorage (cartStore), what was typed in checkout in sessionStorage on every change
// (checkoutDraft, BUG-442), and an open payment step re-opens the same Stripe session (BUG-378).
//
// Never reloaded:
// - while a payment is being confirmed (PaymentStep holds the reload for the confirm), and
// - into or out of the order confirmation page (Stripe's return is a full page load already, so it
//   runs the new release; the shopper reads their receipt in peace).

export const RELOADED_FOR_KEY = 'helin.update.reloadedFor'

let holds = 0

/** Holds any update reload until the returned release is called (once; more calls do nothing). */
export function holdReload(): () => void {
  holds += 1
  let released = false
  return () => {
    if (released) return
    released = true
    holds -= 1
  }
}

export function reloadHeld(): boolean {
  return holds > 0
}

/** The page Stripe returns to after a payment. */
export function isPaymentReturn(pathname: string): boolean {
  return /\/shop\/order-confirmed\/?$/.test(pathname)
}

export interface ReloadGateOptions {
  reload: () => void
  /** sessionStorage; a getter because merely reading window.sessionStorage can throw. */
  storage: () => Pick<Storage, 'getItem' | 'setItem'>
}

export function createReloadGate({ reload, storage }: ReloadGateOptions) {
  let pendingSha: string | null = null

  // The CDN edge can serve the old index.html for a moment after a deploy. Reload once per release:
  // if the reloaded page is still the old build, wait for the next release rather than reloading on
  // every click. Blocked storage means no guard, which only costs an extra reload in that moment.
  function alreadyReloadedFor(sha: string): boolean {
    try {
      return storage().getItem(RELOADED_FOR_KEY) === sha
    } catch {
      return false
    }
  }
  function remember(sha: string) {
    try {
      storage().setItem(RELOADED_FOR_KEY, sha)
    } catch {
      // See above.
    }
  }

  return {
    markPending(sha: string) {
      pendingSha = sha
    },
    pending(): string | null {
      return pendingSha
    },
    /** Called after every navigation; reloads the page (now at `to`) when an update is pending. */
    onRouteChange(from: string, to: string): boolean {
      if (!pendingSha || from === to) return false
      if (reloadHeld() || isPaymentReturn(from) || isPaymentReturn(to)) return false
      if (alreadyReloadedFor(pendingSha)) return false
      remember(pendingSha)
      reload()
      return true
    },
  }
}
